import { Dialog, Grid } from '@material-ui/core';
import { Button } from '@material-ui/core/';
import React from 'react';
import { Link } from 'react-router-dom';

import { ISite, ResourceActiveDisabled, ResourceActiveEnabled } from '~/src/dash/api/console-api';
import { dealActiveStatusChangeHandler } from '~/src/dash/api/deal_samsung';
// specific to this view
import {
  deleteDeal,
  getAllDeals,
  getDealTypeNameFromDealTypeValue,
  IDeal,
  IIndexExchangeToPublicaApiDeal,
  ISamsungDealParams,
  updateDeal,
} from '~/src/dash/api/deals';
import { IDealRowData, IListTableColumn, ListTable } from '~/src/dash/components/list-table';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels, spacing } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteChannelExisting,
  RouteDealEdit,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { headerBidderAdapterKeyToLabel } from '~/src/dash/utils/header-bidder-adapters';
import { obfuscateDealName } from '~/src/dash/utils/obfuscate';
import {
  IPermissionCheck,
  IS_AUTHORIZED,
  notAllowedEmptyIPermissionCheck,
} from '~/src/dash/utils/permission_service';
import { routeName } from '~/src/dash/utils/routes';
import { RowButton } from '~/src/dash/views/component-view';
import { buildDealCloneUrl } from '~/src/dash/views/deal/deal-settings-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  loading: boolean;
  readAuthorization: IPermissionCheck;
  writeAuthorization: IPermissionCheck;
  snackbarMessage: string;
  snackbarError: boolean;
  snackbarWarning: boolean;
  deals: IDeal[];
  updating: boolean;
  openChannelModalTable: boolean;
  channelModalTableList: ISite[];
}

export const DealListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    displayName: 'DealListView';

    constructor(props: IProps) {
      super(props);
      this.state = {
        deals: [] as IDeal[],
        loading: true,
        readAuthorization: notAllowedEmptyIPermissionCheck,
        writeAuthorization: notAllowedEmptyIPermissionCheck,
        snackbarMessage: '',
        snackbarError: false,
        snackbarWarning: false,
        updating: false,
        openChannelModalTable: false,
        channelModalTableList: [] as ISite[],
      };
    }

    // --------------- TABLE NAVIGATION ---------------
    getItemSettingRoute = (id: string): string => {
      return getRoutePath(RouteDealEdit, extractRouteParams(this.props), {
        dealID: parseInt(id, 10),
      });
    };

    // navigates to clone existing deal setting form
    cloneDealById = (id: number): void => {
      window.location.href =
        '/#' + buildDealCloneUrl({ ID: id } as IDeal, this.props.session.publisher.ID);
    };

    // --------------- TABLE HANDLERS ---------------
    handleDeleteDeal = (dealIDStr: string) => {
      const dealID = parseInt(dealIDStr, 10);
      this.setState(
        {
          updating: true,
        },
        () => {
          deleteDeal(dealID, this.props.session.publisher.ID)
            .then(() => {
              const deals = this.state.deals.filter(
                (currentDeal: IDeal) => currentDeal.ID !== dealID,
              );
              this.setState({
                deals,
                snackbarMessage: 'Successfully deleted deal',
                snackbarError: false,
                updating: false,
              });
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting deal',
                snackbarError: true,
                updating: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    handleChangeActive = (itemID: number, checked: boolean): void => {
      if (this.state.updating) {
        return;
      }
      const deals = [...this.state.deals];
      let selectedIndex = -1;
      // find the deal to be updated
      for (let i = 0; i < deals.length; i++) {
        if (deals[i].ID === itemID) {
          deals[i].Active = checked ? ResourceActiveEnabled : ResourceActiveDisabled;
          selectedIndex = i;
          break;
        }
      }
      // create a deal with the fields that need to be updated as well as the ID
      if (selectedIndex >= 0) {
        // if the deal is from Samsung SSP, then we need to apply the active/status logic
        if (deals[selectedIndex].BidderType === 'samsungssp') {
          dealActiveStatusChangeHandler(deals[selectedIndex]);
        }

        this.setState(
          {
            updating: true,
          },
          () => {
            updateDeal(deals[selectedIndex], this.props.session.publisher.ID, true)
              .then(() => {
                this.setState({
                  deals,
                  snackbarMessage: 'Successfully saved deal.',
                  snackbarError: false,
                  updating: false,
                });
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Failed to save deal.',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      }
    };

    // --------------- EVENT NOTIFICATION HANDLER ---------------
    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
        snackbarWarning: false,
      });
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    componentDidMount() {
      const { user, session } = this.props;
      this.setState({
        readAuthorization: IS_AUTHORIZED(['read_deal'], user.user),
        writeAuthorization: IS_AUTHORIZED(['write_deal'], user.user),
      });

      setTimeout(() => {
        this.setState({
          loading: false,
        });
      }, 2000);

      getAllDeals(session.publisher.ID)
        .then((responses: any) => {
          let deals: IDeal[] = [];
          let errorMsg: string = '';
          responses.forEach((resp: any) => {
            if (resp.status === 'fulfilled') {
              deals = deals.concat(resp.value);
            } else {
              if (resp.reason) {
                errorMsg = this.handleGetDealError(resp.reason);
              }
            }
          });

          this.setState(
            {
              deals,
            },
            () => {
              if (errorMsg) {
                if (errorMsg.includes("can't enrich the deal list")) {
                  this.setState({
                    snackbarMessage: errorMsg,
                    snackbarWarning: true,
                  });
                } else {
                  this.setState({
                    snackbarMessage: errorMsg,
                    snackbarError: true,
                  });
                }
              }
            },
          );
        })
        .catch((reason: any) => {
          // ---- An error happened. Display an error message.
          // Create the error message.
          const errorMsg = this.handleGetDealError(reason);

          // Display snackbar.
          if (errorMsg.includes("can't enrich the deal list")) {
            this.setState({
              snackbarMessage: errorMsg,
              snackbarWarning: true,
            });
          } else {
            this.setState({
              snackbarMessage: errorMsg,
              snackbarError: true,
            });
          }
        });
    }

    handleGetDealError(reason: any) {
      let errorMsg: string;

      if (reason.message) {
        // Error message in the response: use it.
        errorMsg = `Error while loading the deal: ${reason.message}`;
        // If the API couldn't enrich the deal list, it means there was some kind of authentication error.
        if (reason.message.includes("can't enrich the deal list")) {
          errorMsg +=
            '\nPlease check your account credentials in the "Manage third-party accounts" panel.';
        }
      } else {
        // No error message in the response: create generic error.
        errorMsg = `Error while loading the deal.`;
      }
      return errorMsg;
    }

    render() {
      const {
        loading,
        snackbarError,
        snackbarWarning,
        snackbarMessage,
        deals,
        writeAuthorization,
        readAuthorization,
        updating,
      } = this.state;

      const columns: IListTableColumn[] = [
        { field: 'Active', title: 'Active', renderType: 'activeswitch', searchable: false },
        { field: 'Name', title: 'Deal Name', renderType: 'namelink' },
        { field: 'DealKey', title: 'Deal ID', renderType: 'namelink' },
        { field: 'BidderType', title: 'Partner', renderType: 'normal' },
        {
          field: 'DealType',
          title: 'Deal Type',
          render: (row: IDealRowData) => this.dealTypeCellRender(row),
        },
        {
          field: 'CustomRender',
          title: 'Targeting',
          render: (row: IDealRowData) => this.dealTargetingCellRender(row),
          searchable: false,
        },
        { field: 'Actions', title: 'Actions', renderType: 'deleteclone', searchable: false },
      ];

      // Transform deal data to ListTable-compatible format
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const data: IDealRowData[] = deals.map((deal: IDeal) => {
        return {
          ID: deal.ID,
          Active: deal.Active === ResourceActiveEnabled,
          Name: obfuscateDealName(deal, user.user.Username, deal.Name),
          DealKey: deal.DealKey || 'N/A',
          DealType: deal.DealType,
          AuctionType: (deal.SspParams as IIndexExchangeToPublicaApiDeal).auctionType,
          BidderType: headerBidderAdapterKeyToLabel(deal.BidderType, this.props.session.publisher),
          Channels: deal.Channels,
          ChannelGroups: deal.ChannelGroups,
          IsActiveSwitchDisabled:
            deal.BidderType === 'samsungssp' &&
            (deal.SspParams as ISamsungDealParams).isDealStopped,
        };
      });

      if (deals.length === 0 && loading) {
        return <LoadingView active={true} text='Loading deals' />;
      }

      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largeHorizontalPadding>
            {/* Channel modal list (opened when user clicks on the targeted channel number)*/}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={this.state.openChannelModalTable}
              onClose={() => {
                this.setState({ openChannelModalTable: false });
              }}
            >
              <ModalSelectTable
                title='Channels'
                items={this.state.channelModalTableList}
                onSelectRow={(channelID: string) => {
                  this.handleModalChannelClick(channelID);
                }}
                interactive={true}
              />
            </Dialog>

            {/* Main view*/}
            <Grid container justify='space-between' style={{ paddingTop: 25, paddingBottom: 40 }}>
              {/* Manage third-party accounts button*/}
              <Grid item>
                <RowButton
                  left={
                    <ModelMediumButton
                      variant='contained'
                      color='primary'
                      style={{ paddingLeft: '30px', paddingRight: '30px' }}
                      component={Link}
                      to={`/app/pub/${user.publisher.ID}/deals/tp_accounts`}
                    >
                      Manage Third Party Accounts
                    </ModelMediumButton>
                  }
                />
              </Grid>
              {/* Create new button*/}
              <Grid item>
                <RowButton
                  right={
                    <ModelMediumButton
                      variant='contained'
                      color='primary'
                      style={{ paddingLeft: '30px', paddingRight: '30px' }}
                      component={Link}
                      to={`/app/pub/${user.publisher.ID}/deals/create`}
                    >
                      + Create New
                    </ModelMediumButton>
                  }
                />
              </Grid>
            </Grid>
            <Grid item style={{ marginBottom: spacing.large }}>
              <ListTable
                title=''
                columns={columns}
                data={data}
                isLoading={loading}
                isUpdating={updating}
                handleChangeActive={this.handleChangeActive}
                getItemSettingRoute={this.getItemSettingRoute}
                handleDeleteItem={this.handleDeleteDeal}
                handleCloneItem={this.cloneDealById}
                handleSetUpdating={this.handleSetUpdating}
                readRoleName={'read_deal'}
                writeRoleName={'write_deal'}
              />
            </Grid>
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              warning={!!snackbarWarning}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }

    // Generates the text to display in the deal table for the "Targeting" column.
    private generateDealTableTargetingText(targetedChannels: ISite[]): string {
      const numberOfTargetedChannels = targetedChannels.length;

      switch (numberOfTargetedChannels) {
        case 0:
          return 'All channels';
        case 1:
          return '1 channel';
        default:
          return `${numberOfTargetedChannels} channels`;
      }
    }

    // Returns a list of all the channels targeted by a deal (both as channel and channel-group).
    private getDealTargetedChannelList(deal: IDealRowData): ISite[] {
      // Create map to store and deduplicate channels IDs.
      const channelDedupMap = new Map();

      // Insert single channels in the map (not the channel-groups).
      for (const channel of deal.Channels) {
        channelDedupMap.set(channel.ID, channel);
      }

      // Insert channel-groups in the map.
      for (const channelGroup of deal.ChannelGroups) {
        for (const channel of channelGroup.Sites) {
          channelDedupMap.set(channel.ID, channel);
        }
      }

      // Return the maps as an array of ISite.
      return Array.from(channelDedupMap.values());
    }

    // Returns the render for the "Targeting" cell of each deal.
    private dealTargetingCellRender(deal: IDealRowData): JSX.Element {
      // Compute the targeted channels for the current deal.
      const targetedChannels = this.getDealTargetedChannelList(deal);

      return (
        <Button
          style={{ textTransform: 'none' }}
          onClick={() => {
            this.setState({
              openChannelModalTable: true,
              channelModalTableList: targetedChannels,
            });
          }}
        >
          {this.generateDealTableTargetingText(targetedChannels)}
        </Button>
      );
    }

    // Returns the render for the "Deal Type" cell of each deal.
    private dealTypeCellRender(deal: IDealRowData): JSX.Element {
      return <>{getDealTypeNameFromDealTypeValue(deal.DealType)}</>;
    }

    // Handles when the user clicks on one channel inside the modal channel list.
    // The modal channel list is displayed when the user clicks on the number of channel targeted in the deal list.
    private handleModalChannelClick(channelID: string) {
      // Navigate to the channel setting page.
      navigate(RouteChannelExisting, extractRouteParams(this.props), {
        ch: channelID,
      });
    }
  },
);
